import { ListItemIcon, MenuItem, MenuList, styled } from '@mui/material';
import cn from 'classnames';
import { useRef, useState } from 'react';
import Iconify from '../../iconify';

type Props = {
  labelChild: React.ReactNode;
  hasChildren: boolean;
  childrenChild: React.ReactNode;
  isSelectable?: boolean;
  selected: boolean;
  disabled?: boolean;
  onClick: (event: React.MouseEvent) => void;
};

export const ExplorerMenuItem = ({
  labelChild,
  hasChildren,
  childrenChild,
  isSelectable = true,
  selected,
  disabled,
  onClick,
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const menuRef = useRef(null);

  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isSelectable) {
      return onClick(e);
    }
    handleExpand(e);
  };

  return (
    <>
      <MenuItem ref={menuRef} disabled={disabled} onClick={handleClick}>
        <ListItemIcon>
          {hasChildren && (
            <StyledAccordionIcon
              className={cn({ expanded })}
              icon="tabler:chevron-right"
              onClick={handleExpand}
            />
          )}
        </ListItemIcon>
        <StyledListItemText>{labelChild}</StyledListItemText>
        {selected && (
          <Iconify
            icon="tabler:check"
            color="success.main"
            ml={2}
            flexShrink={0}
          />
        )}
      </MenuItem>
      {expanded && <MenuList>{childrenChild}</MenuList>}
    </>
  );
};

const StyledAccordionIcon = styled(Iconify)(({ theme }) => ({
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.short,
    easing: theme.transitions.easing.sharp,
  }),
  '&:hover': {
    color: theme.palette.text.primary,
  },
  '&.expanded': {
    transform: 'rotate(90deg)',
  },
}));

export const StyledListItemText = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexGrow: 1,
  overflow: 'hidden',
  gap: theme.spacing(0.75),
  span: {
    ...theme.typography.body2,
    textOverflow: 'ellipsis',
    textWrap: 'nowrap',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  'em-emoji': {
    display: 'flex',
    justifyContent: 'center',
    width: 20,
  },
}));
